import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Mail, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ApplicationSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract customer data from navigation state
  const customerData = location.state?.customerData || {};

  const handleNewApplication = () => {
    // Navigate back to form with customer details for pre-fill
    navigate('/apply', {
      state: { customerData }
    });
  };

  const handleReturnHome = () => {
    // Force top-level redirect to prevent iframe nesting
    if (window.top) {
      window.top.location.href = 'https://listers.com.au';
    } else {
      window.location.replace('https://listers.com.au');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_seller-dashboard-pro-20/artifacts/koljavwk_logo.webp" 
            alt="Listers" 
            className="h-8 w-auto"
          />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-[#C6E76C] rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-[#2C2C2C]" strokeWidth={2.5} />
          </div>
          
          <h1 
            className="font-normal text-[#2C2C2C] mb-4 leading-tight"
            style={{ 
              fontFamily: 'Tilt Warp, sans-serif',
              fontSize: 'clamp(42px, 5vw, 64px)',
              fontWeight: 400
            }}
          >
            Application Submitted!
          </h1>
          
          <p 
            className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 'clamp(18px, 2vw, 20px)',
              fontWeight: 400
            }}
          >
            Thank you for your submission. We've received your application and our team is excited to review it.
          </p>
        </div>

        {/* Next Steps Card */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-200 mb-8">
          <h2 
            className="font-normal text-[#2C2C2C] mb-4"
            style={{
              fontFamily: 'Tilt Warp, sans-serif',
              fontSize: 'clamp(28px, 3vw, 36px)',
              fontWeight: 400
            }}
          >
            What Happens Next?
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#C6E76C] flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-[#2C2C2C] font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>1</span>
              </div>
              <div>
                <p className="text-[#2C2C2C] font-semibold mb-1" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  We Review Your Item
                </p>
                <p className="text-[#6B7280]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  Our team will assess whether your item is a right fit to be sold via our system.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#C6E76C] flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-[#2C2C2C] font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>2</span>
              </div>
              <div>
                <p className="text-[#2C2C2C] font-semibold mb-1" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  You'll Hear From Us
                </p>
                <p className="text-[#6B7280]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  <strong className="text-[#2C2C2C]">Within 3-5 business days</strong>, we'll contact you with a decision via SMS along with next steps. (check JUNK folder)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#C6E76C] flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-[#2C2C2C] font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>3</span>
              </div>
              <div>
                <p className="text-[#2C2C2C] font-semibold mb-1" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  If Approved
                </p>
                <p className="text-[#6B7280]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                  We'll schedule a time for you to drop off your item, set your price and then we'll handle everything from there!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Card */}
        <div className="bg-gradient-to-br from-[#C6E76C] to-[#A8C958] rounded-2xl p-8 shadow-lg border-2 border-[#A8C958] mb-8">
          <h3 
            className="font-normal text-[#2C2C2C] mb-4"
            style={{
              fontFamily: 'Tilt Warp, sans-serif',
              fontSize: 'clamp(24px, 2.5vw, 30px)',
              fontWeight: 400
            }}
          >
            Questions?
          </h3>
          
          <p className="text-[#2C2C2C] mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
            If you have any questions or need to update your application, feel free to reach out:
          </p>
          
          <div className="space-y-3">
            <a 
              href="mailto:gday@listers.com.au"
              className="flex items-center gap-3 p-4 bg-white/90 rounded-lg hover:bg-white transition-all"
            >
              <Mail className="w-5 h-5 text-[#2C2C2C]" />
              <div>
                <p className="text-[#2C2C2C] font-bold" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  Email Us
                </p>
                <p className="text-[#6B7280]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  gday@listers.com.au
                </p>
              </div>
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            onClick={handleNewApplication}
            className="w-full bg-[#2C2C2C] hover:bg-[#1C1C1C] text-white h-auto py-5 rounded-xl font-bold"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Submit Application for Different Item
          </Button>

          <Button
            onClick={handleReturnHome}
            className="w-full bg-[#C6E76C] hover:bg-[#A8C958] text-[#2C2C2C] h-auto py-5 rounded-xl font-bold"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}
          >
            <Home className="w-5 h-5 mr-2" />
            Return to Listers Website
          </Button>
        </div>
      </div>
    </div>
  );
}
